import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { SELF_REGISTERABLE_ROLES } from '../../auth/types';
import type { Role } from '../../auth/types';
import { HOME_ROUTE_FOR_ROLE } from '../../routes/roleHome';
import './AuthForm.css';

const ROLE_LABELS: Record<(typeof SELF_REGISTERABLE_ROLES)[number], string> = {
  buyer: 'Buyer, discover and buy tickets',
  investor: 'Investor, invest in productions',
  organizer: 'Organizer, run events',
  artist: 'Artist, get booked',
  agency: 'Agency, represent artists',
  venue: 'Venue, host events',
  sponsor: 'Sponsor, sponsor events',
};

/** The engine enforces a 10-character minimum; mirror it for fast feedback. */
const MIN_PASSWORD_LENGTH = 10;

export function RegisterPage() {
  const { register, user, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'buyer' as Role,
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (!loading && user) {
    return <Navigate to={HOME_ROUTE_FOR_ROLE[user.role]} replace />;
  }

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (form.password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const created = await register({
        email: form.email,
        password: form.password,
        role: form.role,
        firstName: form.firstName || undefined,
        lastName: form.lastName || undefined,
      });
      navigate(HOME_ROUTE_FOR_ROLE[created.role], { replace: true });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-form">
      <div className="auth-form__card card">
        <h1 className="auth-form__title">Create your account</h1>
        <p className="auth-form__subtitle">Pick the role that fits how you use Voleto.</p>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          {formError && (
            <p className="alert alert--error auth-form__error" role="alert">
              {formError}
            </p>
          )}

          <div className="auth-form__row">
            <div className="field">
              <label className="field__label" htmlFor="register-first">
                First name
              </label>
              <input
                id="register-first"
                className="field__input"
                autoComplete="given-name"
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="register-last">
                Last name
              </label>
              <input
                id="register-last"
                className="field__input"
                autoComplete="family-name"
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="register-email">
              Email
            </label>
            <input
              id="register-email"
              className="field__input"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="register-password">
              Password
            </label>
            <input
              id="register-password"
              className="field__input"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
            />
            <p className="field__hint">At least {MIN_PASSWORD_LENGTH} characters.</p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="register-role">
              I am a…
            </label>
            <select
              id="register-role"
              className="field__input"
              value={form.role}
              onChange={(e) => update('role', e.target.value as Role)}
            >
              {SELF_REGISTERABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="button button--primary button--block"
            disabled={submitting}
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="auth-form__switch">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
